import { Component, DestroyRef, ElementRef, PLATFORM_ID, Renderer2, ViewContainerRef, computed, inject, signal, viewChild } from "@angular/core";

import { AbstractControl, FormBuilder, FormControl, FormGroup, Validators } from "@angular/forms";

import { Title } from "@angular/platform-browser";
import { ActivatedRoute, Router } from "@angular/router";
import { isPlatformBrowser } from "@angular/common";

import { FacebookLoginProvider, GoogleLoginProvider, SocialAuthService } from "@abacritt/angularx-social-login";

import { NgxSpinnerService } from "ngx-spinner";
import * as forge from "node-forge";

import { ForgotpasswordComponent } from "@auth/forgotpassword/forgotpassword.component";

import { LoginService } from "@services/auth.service";
import { CommonService } from "@services/common.service";
import { SharedService } from "@services/shared.service";
import { YTBSEOService } from "@services/ytb-seo.service";
import { RegistrationService } from "@services/registration.service";

import { DynamicDialogConfig, DynamicDialogRef } from "primeng/dynamicdialog";

import Swal from "sweetalert2";

import { MustMatch } from "../sign-up/customvalidation/mustmatchvalidation";
import { Changepwd } from "@models/changepwd";
import { Social_Providers } from "app/lookup-config";

import { takeUntilDestroyed } from "@angular/core/rxjs-interop";

declare const google: any;

/* ============================================================
 * Types
 * ============================================================ */

interface LoginFormModel {
  userId: FormControl<string>;
  password: FormControl<string>;
  actionType: FormControl<number>;
  isRememberMe: FormControl<boolean | null>;
}

interface ResetFormModel {
  password: FormControl<string>;
  confirmpassowrd: FormControl<string>;
  actionType: FormControl<number>;
}

@Component({
  selector: "app-user",
  templateUrl: "./user.component.html",
  styleUrls: ["./user.component.scss"],
})
export class UserComponent {
  /* ============================================================
   * Angular injected dependencies
   * ============================================================ */

  private readonly fb = inject(FormBuilder);

  private readonly loginservice = inject(LoginService);
  private readonly elem = inject(ElementRef);
  private readonly renderer = inject(Renderer2);
  private readonly spinner = inject(NgxSpinnerService);
  private readonly shared = inject(SharedService);
  private readonly router = inject(Router);
  private readonly authService = inject(SocialAuthService);
  private readonly ytbseo = inject(YTBSEOService);
  private readonly title = inject(Title);
  private readonly commonService = inject(CommonService);
  private readonly route = inject(ActivatedRoute);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly regService = inject(RegistrationService);
  private readonly destroyRef = inject(DestroyRef);

  /*
   * Optional PrimeNG DynamicDialog dependencies.
   *
   * Angular's inject() supports optional dependencies.
   */
  public readonly ref = inject(DynamicDialogRef, { optional: true });
  public readonly config = inject(DynamicDialogConfig, { optional: true });

  /* ============================================================
   * ViewChild -> viewChild()
   * ============================================================ */

  readonly closebtn = viewChild<ElementRef>("closebtn");

  readonly loginbtn = viewChild<ElementRef>("loginbtn");

  // readonly load = viewChild.required<ViewContainerRef>("load");
  readonly load = viewChild.required("load", {
    read: ViewContainerRef,
  });
  readonly forgotpasswordElement = viewChild<ElementRef>("forgotpasswordElement");

  readonly forgotel = viewChild<ElementRef>("forgot");

  readonly googleBtnContainer = viewChild.required<ElementRef>("googleBtnContainer");

  /* ============================================================
   * Signals - UI state
   * ============================================================ */

  readonly showSuccess = signal(false);

  readonly showFail = signal(false);

  readonly submitted = signal(false);

  readonly showreset = signal(false);

  readonly showlogin = signal(true);

  readonly password = signal(false);

  readonly invaliduser = signal(false);

  readonly isNavigate = signal(false);

  readonly rememeberMe = signal(false);

  readonly showErrorMessage = signal(false);

  readonly showSuccessMessage = signal(false);

  readonly isCoupon = signal(false);

  readonly isConfirmationEmail = signal(false);

  readonly forgotPassword = signal(true);

  readonly displaySignin = signal(true);

  readonly fromModal = signal(false);

  /* ============================================================
   * Signals - data
   * ============================================================ */

  readonly userid = signal<unknown>(null);

  readonly errorMessageInfo = signal<string[]>([]);

  readonly key = signal("");

  readonly previousPassword = signal("");

  readonly loginResult = signal<any>(null);

  readonly jwtToken = signal<string | null>(null);

  /* ============================================================
   * Computed signals
   * ============================================================ */

  readonly hasErrorMessage = computed(() => this.showErrorMessage() && this.errorMessageInfo().length > 0);

  readonly hasSuccessMessage = computed(() => this.showSuccessMessage() && this.errorMessageInfo().length > 0);

  /* ============================================================
   * Forms
   * ============================================================ */

  readonly loginForm: FormGroup<LoginFormModel> = this.fb.group({
    userId: this.fb.nonNullable.control("", {
      validators: [Validators.required, Validators.maxLength(50), this.commonService.spaceValidator.bind(this.commonService)],
    }),

    password: this.fb.nonNullable.control("", {
      validators: [Validators.required],
    }),

    actionType: this.fb.nonNullable.control(0),

    isRememberMe: this.fb.control<boolean | null>(null),
  });

  readonly resetForm: FormGroup<ResetFormModel> = this.fb.group(
    {
      password: this.fb.nonNullable.control("", {
        validators: [Validators.required],
      }),

      confirmpassowrd: this.fb.nonNullable.control("", {
        validators: [Validators.required],
      }),

      actionType: this.fb.nonNullable.control(1),
    },
    {
      validators: MustMatch("password", "confirmpassowrd"),
    },
  );

  /* ============================================================
   * Convenience getter
   * ============================================================ */

  get f() {
    return this.loginForm.controls;
  }

  /* ============================================================
   * Constructor replacement
   *
   * Angular executes this during construction.
   * ============================================================ */

  constructor() {
    this.initializeSubscriptions();
  }

  /* ============================================================
   * Initialization
   * ============================================================ */

  private initializeSubscriptions(): void {
    /*
     * SEO
     */
    this.route.queryParams.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      const url = this.router.url.split("?");

      setTimeout(() => {
        this.ytbseo.createCanonicalURL(this.router.url);

        this.ytbseo.setDynamicTitle(url[0]);
      }, 3000);
    });

    /*
     * Hide spinner
     */
    this.spinner.hide();

    /*
     * User menu roles
     */
    this.regService.GetUserMenuRolesB2CUser$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((payload) => {
      console.log(JSON.stringify(payload));

      if (!payload) {
        return;
      }

      this.spinner.hide();

      if (payload?.successCode) {
        this.hideLogin();

        this.enableMyAccount();

        this.shared.setMenuListDetails(payload?.menuV1List);
      }
    });

    /*
     * Login success
     */
    this.loginservice.LoginSuccess.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((payload) => {
      console.log(JSON.stringify(payload));

      this.spinner.hide();

      if (!payload) {
        this.setErrorMessage(["Unable to perform this operation at this time"]);

        return;
      }

      this.isConfirmationEmail.set(false);

      /*
       * Password reset required
       */
      if (payload?.successCode && payload?.result?.isReset) {
        this.showreset.set(true);

        this.showlogin.set(false);

        this.loginResult.set(payload?.result);

        return;
      }

      /*
       * Normal login
       */
      if (payload?.successCode && !payload?.result?.isReset) {
        this.shared.setUserIdAfterLogin(payload.result.userName);

        this.shared.setLoginUserDetails(payload.result);

        this.setSuccessMessage(payload.messageInfo.uiSuccessMessge);

        this.shared.setOrgCodeDetails(payload?.result?.orgCode);

        this.shared.setMenuListDetails(payload?.result?.menuV1List);

        this.shared.closeRefereFrom();

        setTimeout(() => {
          this.clearMessages();

          this.hideLogin();

          this.enableMyAccount();

          this.loginForm.controls.userId.reset();

          this.loginForm.controls.password.reset();
        }, 200);

        return;
      }

      /*
       * Login failed
       */
      if (!payload.successCode) {
        this.setErrorMessage(payload.messageInfo.uiErrorMessage);

        if (payload.messageInfo.errorCode === "CMER012") {
          this.isConfirmationEmail.set(true);
        }

        return;
      }

      this.setErrorMessage(["Unable to perform this operation at this time"]);
    });

    /*
     * Login service failure
     */
    this.loginservice.onFailure.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((response) => {
      if (!response) {
        return;
      }

      this.spinner.hide();

      this.commonService.logError({
        logLevel: "Error page Navigation Before",
        message: "Login Service OnFailure From User Component",
      });

      if (response.exceptionSource === "Login_Issue") {
        this.router.navigate(["error"]);
      }
    });

    /*
     * Forgot password state
     */
    this.shared.forgotchangedtls$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      if (params !== "login") {
        return;
      }

      this.removeForgotClasses();

      this.forgotPassword.set(true);
    });

    /*
     * Logout
     */
    this.shared.logOutUserId.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((status) => {
      if (status) {
        this.loginForm.controls.userId.reset();

        this.loginForm.controls.password.reset();
      }
    });

    /*
     * Social authentication
     */
    this.authService.authState.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((user) => {
      console.log("== user == Auth State", user);

      setTimeout(() => {
        this.closePopUp();

        if (user) {
          this.saveResponse(user);
        }
      }, 3000);
    });

    /*
     * Social login success
     */
    this.loginservice.SocialLoginSuccess.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((response) => {
      if (response) {
        // Existing behavior intentionally preserved.
      }
    });

    /*
     * Social auth initialization
     */
    this.authService.initState.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      // Existing behavior intentionally preserved.
    });

    /*
     * Resend email confirmation
     */
    this.commonService.RESENDEMAILCONFIRMATIONSSUCCESS.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((res) => {
      if (!res) {
        return;
      }

      this.spinner.hide();

      this.isConfirmationEmail.set(false);

      if (res.successCode) {
        this.setSuccessMessage(res.messageInfo.uiSuccessMessge);
      } else {
        this.setErrorMessage(res.messageInfo.uiErrorMessage);
      }
    });

    /*
     * Encryption key
     */
    this.commonService.keySuccess$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((key) => {
      if (key) {
        this.key.set(key.data);
      }
    });

    /*
     * Change password
     */
    this.loginservice.ChangePasswordSuccess.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((payload) => {
      if (!payload) {
        return;
      }

      this.spinner.hide();

      if (payload.status !== "Success") {
        return;
      }

      const result = this.loginResult();

      this.shared.setUserIdAfterLogin(result.userName);

      this.shared.setLoginUserDetails(result);

      this.setSuccessMessage(payload?.messageInfo?.uiSuccessMessge);

      this.shared.setOrgCodeDetails(result?.orgCode);

      this.shared.setMenuListDetails(result?.menuV1List);

      Swal.fire({
        imageUrl: "assets/images/envelope.png",

        title: this.errorMessageInfo()[0],

        allowOutsideClick: false,

        text: "Click Ok to Continue",
      }).then(() => {
        this.clearMessages();

        this.hideLogin();

        this.enableMyAccount();

        this.resetForm.reset();
      });
    });

    /*
     * Initial form reset
     */
    this.resetLoginForms();
  }

  /* ============================================================
   * Message helpers
   * ============================================================ */

  private clearMessages(): void {
    this.errorMessageInfo.set([]);

    this.showErrorMessage.set(false);

    this.showSuccessMessage.set(false);
  }

  private setErrorMessage(messages: string[] | undefined): void {
    this.errorMessageInfo.set(messages ?? ["Unable to perform this operation at this time"]);

    this.showErrorMessage.set(true);

    this.showSuccessMessage.set(false);
  }

  private setSuccessMessage(messages: string[] | undefined): void {
    this.errorMessageInfo.set(messages ?? []);

    this.showSuccessMessage.set(true);

    this.showErrorMessage.set(false);
  }

  private resetLoginForms(): void {
    this.loginForm.controls.userId.reset();

    this.loginForm.controls.password.reset();
  }

  /* ============================================================
   * Encryption
   * ============================================================ */

  encryptWithPublicKey(dataToEncrypt: string): void {
    const aesKey = forge.random.getBytesSync(32);

    const publicKey = forge.pki.publicKeyFromPem(this.key());

    const encryptedAesKey = publicKey.encrypt(aesKey, "RSAES-PKCS1-V1_5");

    const dataBytes = forge.util.encodeUtf8(dataToEncrypt);

    const aesCipher = forge.cipher.createCipher("AES-CTR", aesKey);

    aesCipher.start({
      iv: forge.random.getBytesSync(32),
    });

    aesCipher.update(forge.util.createBuffer(dataBytes));

    aesCipher.finish();

    const encryptedData = aesCipher.output.getBytes();

    console.log("encryptedData :", encryptedData);

    const encryptedDataBase64 = forge.util.encode64(encryptedData);

    const encryptedAesKeyBase64 = forge.util.encode64(encryptedAesKey);

    console.log("Encrypted data:", encryptedDataBase64);

    console.log("Encrypted AES key:", encryptedAesKeyBase64);
  }

  /* ============================================================
   * View initialization
   * ============================================================ */

  ngAfterViewInit(): void {
    this.resetLoginForms();

    this.waitForGoogle().then(() => this.initializeGoogleButton());
  }

  /* ============================================================
   * Google
   * ============================================================ */

  waitForGoogle(): Promise<void> {
    return new Promise((resolve) => {
      const checkGoogle = () => {
        if (typeof google !== "undefined" && google.accounts?.id) {
          resolve();
        } else {
          setTimeout(checkGoogle, 100);
        }
      };

      checkGoogle();
    });
  }

  initializeGoogleButton(): void {
    const container = this.googleBtnContainer();

    if (!container) {
      return;
    }

    const clientId = Social_Providers.find((x) => x.provider === "google")?.value;

    google.accounts.id.initialize({
      client_id: clientId,

      callback: (response: any) => {
        console.log("Google ID callback ClientID response:", response);

        this.handleCredentialResponse(response);
      },
    });

    google.accounts.id.renderButton(container.nativeElement, {
      theme: "outline",
      size: "large",
    });

    google.accounts.id.prompt((notification: any) => {
      console.log("Google One Tap prompt notification:", notification);
    });
  }

  handleCredentialResponse(response: any): void {
    console.log("JWT token", response.credential);

    this.jwtToken.set(response.credential);

    const base64Url = response.credential.split(".")[1];

    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");

    const decodedPayload = JSON.parse(atob(base64));

    console.log("Decoded JWT payload:", decodedPayload);

    const user = {
      provider: "google",

      email: decodedPayload.email,

      firstName: decodedPayload.given_name,

      lastName: decodedPayload.family_name,

      picture: decodedPayload.picture,

      id: decodedPayload.sub,
    };

    this.saveResponse(user);
  }

  /* ============================================================
   * Signup
   * ============================================================ */

  navigateToSignup(): void {
    this.shared.navigateToSignupFromSigning(true);
  }

  /* ============================================================
   * Terms / Privacy
   * ============================================================ */

  navigateToPrivacyPolicy(link: string): void {
    let url = "";

    if (link === "terms") {
      url = this.router.serializeUrl(this.router.createUrlTree(["terms-and-conditions"]));
    }

    if (link === "privacy") {
      url = this.router.serializeUrl(this.router.createUrlTree(["privacy-policy"]));
    }

    if (isPlatformBrowser(this.platformId) && typeof window !== "undefined") {
      window.open(url, "_blank");
    }
  }

  /* ============================================================
   * Close login
   * ============================================================ */

  closePopUp(): void {
    this.isConfirmationEmail.set(false);

    this.errorMessageInfo.set([]);

    this.showErrorMessage.set(false);

    setTimeout(() => {
      this.displaySignin.update((value) => !value);
    });
  }

  /* ============================================================
   * Login
   * ============================================================ */

  submitLogin(): void {
    this.errorMessageInfo.set([]);

    this.showErrorMessage.set(false);

    this.isConfirmationEmail.set(false);

    this.submitted.set(true);

    if (!this.loginForm.valid) {
      this.commonService.validateAllFormFields(this.loginForm);

      return;
    }

    this.submitted.set(false);

    this.rememeberMe.set(this.loginForm.controls.isRememberMe.value ?? false);

    this.spinner.show();

    /*
     * Normal login
     */
    if (!this.showreset()) {
      console.log(this.key());

      console.log(this.loginForm.value);

      const loginObj = this.loginForm.getRawValue();

      const encryptedPassword = this.commonService.encryptData(JSON.stringify(loginObj.password), this.key());

      loginObj.password = encryptedPassword;

      this.previousPassword.set(encryptedPassword);

      this.loginservice.newLogin(loginObj);

      return;
    }

    /*
     * Password reset login
     */

    const password = this.commonService.encryptData((this.resetForm.controls.password.value), this.key());

    const confirmPassword = this.commonService.encryptData((this.resetForm.controls.confirmpassowrd.value), this.key());

    const loginData = {
      ...this.resetForm.getRawValue(),
      password,
      confirmpassowrd: confirmPassword,
    };

    this.loginservice.newLogin(loginData);
  }

  /* ============================================================
   * Validate token
   * ============================================================ */

  validateToken(): void {
    this.errorMessageInfo.set([]);

    this.showErrorMessage.set(false);

    this.loginservice.validateToken();
  }

  /* ============================================================
   * Login UI
   * ============================================================ */

  hideLogin(): void {
    this.errorMessageInfo.set([]);

    this.showErrorMessage.set(false);

    this.showSuccess.set(false);
  }

  enableMyAccount(): void {
    this.errorMessageInfo.set([]);

    this.showErrorMessage.set(false);

    if (this.config?.data?.["fromModal"]) {
      console.log("From Modal window");

      this.shared.closeModalWindowAfterLogin$.next(true);
    } else {
      this.router.navigate(["/my-account"]);
    }

    this.shared.loginSuccess.next(false);
  }

  /* ============================================================
   * Forgot password
   * ============================================================ */

  cancel(): void {
    if (isPlatformBrowser(this.platformId) && typeof window !== "undefined") {
      window.scrollTo(0, 0);
    }

    this.errorMessageInfo.set([]);

    this.showErrorMessage.set(false);

    this.removeForgotClasses();

    this.forgotPassword.set(true);
  }

  private removeForgotClasses(): void {
    const forgot = this.forgotel();

    const forgotPassword = this.forgotpasswordElement();

    if (forgot) {
      this.renderer.removeClass(forgot.nativeElement, "is-flipped");
    }

    if (forgotPassword) {
      this.renderer.removeClass(forgotPassword.nativeElement, "position-relative");
    }
  }

  /* ============================================================
   * Dynamic component loading
   *
   * ComponentFactoryResolver is no longer required.
   * ============================================================ */

  loadComponent(typeofhelp: string): void {
    if (isPlatformBrowser(this.platformId) && typeof window !== "undefined") {
      window.scrollTo(0, 0);
    }

    this.errorMessageInfo.set([]);

    this.showErrorMessage.set(false);

    const component = this.getComponent(typeofhelp);

    if (!component) {
      return;
    }

    const container = this.load();

    container.clear();

    container.createComponent(component);

    const forgot = this.forgotel();

    const forgotPassword = this.forgotpasswordElement();

    if (forgot) {
      this.renderer.addClass(forgot.nativeElement, "is-flipped");
    }

    if (forgotPassword) {
      this.renderer.addClass(forgotPassword.nativeElement, "position-relative");
    }
  }

  getComponent(component: string): typeof ForgotpasswordComponent | undefined {
    this.errorMessageInfo.set([]);

    this.showErrorMessage.set(false);

    if (component === "forgotpassword") {
      this.forgotPassword.set(false);

      return ForgotpasswordComponent;
    }

    return undefined;
  }

  /* ============================================================
   * Reset password
   * ============================================================ */

  resetlogin(): void {
    this.errorMessageInfo.set([]);

    this.showErrorMessage.set(false);

    this.isConfirmationEmail.set(false);

    this.submitted.set(true);

    if (!this.resetForm.valid) {
      this.commonService.validateAllFormFields(this.resetForm);

      return;
    }

    this.submitted.set(false);

    this.spinner.show();

    this.commonService.getKey();

    const password = this.commonService.encryptData((this.resetForm.controls.password.value), this.key());

    const result = this.loginResult();

    const newpwdObj: any = {
      userId: result.userName,

      userRole: result.userRole,

      roletypeCode: result.roleTypeCode,

      projectType: "B2C",

      currentPassword: this.previousPassword(),

      newPassword: password,
    };

    this.loginservice.changenewpwd(newpwdObj);
  }

  /* ============================================================
   * Resend confirmation email
   * ============================================================ */

  resendEmailConfirmation(): void {
    this.invaliduser.set(false);

    this.errorMessageInfo.set([]);

    this.isConfirmationEmail.set(false);

    this.submitted.set(true);

    if (this.loginForm.valid) {
      this.spinner.show();

      this.commonService.resendEmailConfirmation(this.loginForm.getRawValue());
    } else {
      this.commonService.validateAllFormFields(this.loginForm);
    }
  }

  /* ============================================================
   * Social login
   * ============================================================ */

  loginwithGoogle(socialProvider: string): void {
    this.isConfirmationEmail.set(false);

    this.errorMessageInfo.set([]);

    this.showErrorMessage.set(false);

    let socialPlatformProvider: string;

    if (socialProvider === "facebook") {
      socialPlatformProvider = FacebookLoginProvider.PROVIDER_ID;
    } else {
      socialPlatformProvider = this.jwtToken() ? this.jwtToken()! : GoogleLoginProvider.PROVIDER_ID;
    }

    console.log(socialPlatformProvider);

    this.authService.signIn(socialPlatformProvider);
  }

  saveResponse(socialUsers: any): void {
    this.isConfirmationEmail.set(false);

    this.errorMessageInfo.set([]);

    this.showErrorMessage.set(false);

    console.log(JSON.stringify(socialUsers));

    this.shared.setUserIdAfterLogin(socialUsers.email);

    const userObj = {
      ProviderName: socialUsers.provider,

      ProviderId: socialUsers.id,

      FirstName: socialUsers.firstName,

      LastName: socialUsers.lastName,

      MiddleName: "",

      Email: socialUsers.email,

      Image: socialUsers.photoUrl,

      Status_Code: 1,

      Device_Type: 1,

      userName: socialUsers.email,

      UserId: socialUsers.email,
    };

    this.shared.setLoginUserDetails(userObj);

    this.spinner.show();

    this.regService.getB2CUserRoles(userObj);
  }

  /* ============================================================
   * Validation helper
   * ============================================================ */

  getError(control: AbstractControl | null, name: string): string | null {
    if (control?.touched && control.errors?.["required"]) {
      return `${name} is required.`;
    }

    if (!control?.errors) {
      return null;
    }

    if (control.errors["minlength"]) {
      return "Please enter minimum 3 characters";
    }

    if (control.errors["pattern"]) {
      return `${name} must be valid [a-b and A-B] only`;
    }

    if (control.errors["space"]) {
      return "Only one space allows between the letters";
    }

    return null;
  }

  /* ============================================================
   * Destroy
   * ============================================================ */

  ngOnDestroy(): void {
    this.spinner.hide();

    this.loginservice.dispose();

    this.loginservice.TokenValid.next(undefined);
  }
}
